import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Star, CheckCircle2, ExternalLink, Copy, Check } from 'lucide-react';
import { publicAvisApi } from '../../lib/api';
import { AssistantAvis } from './AssistantAvis';

/**
 * Page publique de dépôt d'avis (module Avis, lot 1). Aucune authentification : ouverte via le lien à
 * jeton unique envoyé au client à la clôture de sa prestation. Note obligatoire (1-5 étoiles), commentaire
 * et affichage du nom optionnels, pas de photo.
 */
export const AvisPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [done, setDone] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);

    const [companyName, setCompanyName] = React.useState('');
    const [logoUrl, setLogoUrl] = React.useState<string | null>(null);
    const [brandColor, setBrandColor] = React.useState('#987b55');
    const [firstName, setFirstName] = React.useState('');

    const [rating, setRating] = React.useState(0);
    const [hover, setHover] = React.useState(0);
    const [comment, setComment] = React.useState('');
    const [showName, setShowName] = React.useState(true);
    const [displayName, setDisplayName] = React.useState('');
    // Avis Google (2026-09-23) : proposé APRÈS le dépôt, à TOUS les clients quelle que soit leur note.
    // Filtrer sur la note serait du « review gating », interdit par Google.
    const [googleUrl, setGoogleUrl] = React.useState('');
    // Assistant de rédaction (2026-09-23) : le client ne tape rien, il choisit un texte proposé à partir
    // de ses réponses. On garde la trace (et ses réponses) avec l'avis.
    const [assiste, setAssiste] = React.useState(false);
    const [reponsesAssistant, setReponsesAssistant] = React.useState<any>(null);
    const [ecritureOuverte, setEcritureOuverte] = React.useState(false);
    // L'assistant est un module ouvert par le super-admin, etablissement par etablissement.
    const [assistantDispo, setAssistantDispo] = React.useState(false);
    // Le client a répondu « non, j'écris moi-même » : on n'insiste pas, le champ s'ouvre.
    const [assistantRefuse, setAssistantRefuse] = React.useState(false);
    // Ce qui a été nettoyé, quand l'établissement l'a enregistré : l'assistant s'en sert au lieu de le
    // demander. Vide quand l'invitation part d'une simple adresse email.
    const [prestationConnue, setPrestationConnue] = React.useState('');
    const [objetsCatalogue, setObjetsCatalogue] = React.useState<string[]>([]);
    const [clientPro, setClientPro] = React.useState(false);
    const [copie, setCopie] = React.useState(false);
    const [commentaireEnvoye, setCommentaireEnvoye] = React.useState('');

    React.useEffect(() => {
        if (!token) return;
        publicAvisApi.getInfo(token).then((d: any) => {
            if (d?.error) { setError(d.error); return; }
            if (d.available === false) { setError("La collecte d'avis n'est plus active pour cet établissement."); return; }
            setCompanyName(d.company_name || '');
            setLogoUrl(d.logo_url || null);
            setBrandColor(d.brand_color || '#987b55');
            setFirstName(d.first_name || '');
            setDisplayName(d.first_name || '');
            setDone(!!d.already_submitted);
            setGoogleUrl(d.google_review_url || '');
            setAssistantDispo(!!d.assistant);
            setPrestationConnue(d.prestation || '');
            setObjetsCatalogue(Array.isArray(d.objets_catalogue) ? d.objets_catalogue : []);
            setClientPro(String(d.client_type || '').toUpperCase().startsWith('PRO'));
        }).catch(() => setError('Lien invalide.')).finally(() => setLoading(false));
    }, [token]);

    const handleSubmit = async () => {
        if (!token || rating < 1) return;
        setSubmitting(true);
        setError('');
        try {
            const res: any = await publicAvisApi.submit(token, {
                rating,
                comment: comment.trim(),
                show_name: showName,
                display_name: showName ? displayName.trim() : '',
                assiste,
                assistant_reponses: assiste ? reponsesAssistant : null,
            });
            if (res?.error) { setError(res.error); return; }
            // Le commentaire est copié dans le presse-papier : sur Google, le client n'a plus qu'à coller.
            const texte = comment.trim();
            setCommentaireEnvoye(texte);
            if (texte && googleUrl) {
                try { await navigator.clipboard?.writeText(texte); setCopie(true); } catch (e) { /* refusé par le navigateur : le bouton « Copier » reste */ }
            }
            setDone(true);
        } catch (e: any) {
            setError(e?.message || "L'envoi a échoué. Réessayez.");
        } finally {
            setSubmitting(false);
        }
    };

    const card: React.CSSProperties = {
        width: '100%', maxWidth: 460, background: '#fff', borderRadius: 16, padding: 32,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 12px 32px -12px rgba(0,0,0,0.14)',
    };
    const label: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: '#78736b', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 };

    return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ecebe6', fontFamily: "-apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif", padding: 16 }}>
            <div style={card}>
                {loading ? (
                    <Loader2 className="animate-spin" style={{ margin: '0 auto', display: 'block', color: '#b8b4ac' }} size={28} />
                ) : error ? (
                    <p style={{ color: '#b3453a', fontSize: 14, textAlign: 'center' }}>{error}</p>
                ) : done ? (
                    <div style={{ textAlign: 'center' }}>
                        <CheckCircle2 size={40} style={{ color: brandColor, margin: '0 auto 12px' }} />
                        <h1 style={{ fontSize: 19, fontWeight: 700, color: '#1c1a17', margin: '0 0 8px' }}>Merci pour votre avis&nbsp;!</h1>
                        <p style={{ fontSize: 14, color: '#78736b', margin: 0 }}>
                            Votre retour a bien été transmis{companyName ? ` à ${companyName}` : ''}.
                        </p>
                        {googleUrl && (
                            <div style={{ marginTop: 22, paddingTop: 20, borderTop: '1px solid #ece8e0', textAlign: 'left' }}>
                                <div style={{ fontWeight: 700, fontSize: 15, color: '#1c1a17', marginBottom: 6 }}>
                                    Vous pouvez aussi le publier sur Google
                                </div>
                                <p style={{ fontSize: 13.5, color: '#3d3a35', lineHeight: 1.6, margin: '0 0 14px' }}>
                                    {commentaireEnvoye
                                        ? 'Votre commentaire est copié : sur Google, il ne vous restera qu’à le coller et à valider.'
                                        : 'Cela prend quelques secondes et aide beaucoup l’établissement.'}
                                </p>
                                {commentaireEnvoye && (
                                    <button type="button"
                                        onClick={async () => { try { await navigator.clipboard?.writeText(commentaireEnvoye); setCopie(true); } catch (e) { /* ignoré */ } }}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 0, padding: 0, marginBottom: 12, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: brandColor }}>
                                        {copie ? <><Check size={14} /> Commentaire copié</> : <><Copy size={14} /> Le copier à nouveau</>}
                                    </button>
                                )}
                                <a href={googleUrl} target="_blank" rel="noopener noreferrer"
                                    onClick={() => {
                                        // La copie se fait ICI, dans le geste du client : Safari (et Chrome
                                        // après un délai) refusent une copie lancée plus tôt, sans geste.
                                        // Le client arrive donc sur Google avec son texte déjà copié.
                                        if (commentaireEnvoye) {
                                            try { navigator.clipboard?.writeText(commentaireEnvoye); setCopie(true); } catch (e) { /* le bouton « Copier » reste */ }
                                        }
                                        if (token) publicAvisApi.clicGoogle(token).catch(() => { });
                                    }}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '13px 16px', borderRadius: 10, background: brandColor, color: '#fff', fontWeight: 700, fontSize: 14.5, textDecoration: 'none' }}>
                                    Publier aussi sur Google <ExternalLink size={15} />
                                </a>
                                <p style={{ fontSize: 12, color: '#a8a29a', lineHeight: 1.5, margin: '10px 0 0' }}>
                                    Vous publiez ce que vous voulez, sous votre compte Google. Votre avis ci-dessus reste transmis dans tous les cas.
                                </p>
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <div style={{ marginBottom: 20 }}>
                            {logoUrl && <img src={logoUrl} alt={companyName} style={{ maxHeight: 56, maxWidth: 200, objectFit: 'contain', display: 'block', marginBottom: 10 }} />}
                            <div style={{ fontWeight: 700, fontSize: 16, color: '#1c1a17' }}>{companyName}</div>
                            <div style={{ height: 2, width: 34, background: brandColor, marginTop: 10, borderRadius: 2 }} />
                        </div>

                        <h1 style={{ fontSize: 19, fontWeight: 700, color: '#1c1a17', margin: '0 0 6px', letterSpacing: '-0.01em' }}>
                            {firstName ? `${firstName}, votre avis compte` : 'Votre avis compte'}
                        </h1>
                        <p style={{ fontSize: 14, color: '#3d3a35', lineHeight: 1.6, margin: '0 0 22px' }}>
                            Comment s'est passée votre prestation&nbsp;? Cela ne prend qu'une minute.
                        </p>

                        <label style={label}>Votre note</label>
                        <div style={{ display: 'flex', gap: 6, marginBottom: 22 }} onMouseLeave={() => setHover(0)}>
                            {[1, 2, 3, 4, 5].map((n) => (
                                <button key={n} type="button" aria-label={`${n} étoile${n > 1 ? 's' : ''}`}
                                    onMouseEnter={() => setHover(n)} onClick={() => setRating(n)}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, lineHeight: 0 }}>
                                    <Star size={34} strokeWidth={1.5}
                                        style={{ color: brandColor, fill: (hover || rating) >= n ? brandColor : 'transparent', transition: 'fill 0.1s' }} />
                                </button>
                            ))}
                        </div>

                        {/* L'assistant apparaît dès que la note est choisie, quelle que soit cette note :
                            le proposer aux seuls clients contents serait du filtrage d'avis, interdit. Le
                            champ d'écriture reste replié — le but est que personne n'ait à taper. */}
                        {rating > 0 && token && assistantDispo && !assistantRefuse && !comment && (
                            <AssistantAvis token={token} note={rating} couleur={brandColor} prestation={prestationConnue} objetsCatalogue={objetsCatalogue} pro={clientPro}
                                onTexte={(texte, reponses) => { setComment(texte); setAssiste(true); setReponsesAssistant(reponses); setEcritureOuverte(false); }}
                                onRefus={() => { setAssistantRefuse(true); setEcritureOuverte(true); }} />
                        )}

                        {(comment || ecritureOuverte || rating === 0 || !assistantDispo) ? (
                            <>
                                <label style={label} htmlFor="avis-comment">
                                    Votre commentaire <span style={{ textTransform: 'none', fontWeight: 400, color: '#a8a29a' }}>(facultatif)</span>
                                </label>
                                <textarea id="avis-comment" value={comment}
                                    onChange={(e) => { setComment(e.target.value); if (assiste) setAssiste(true); }} rows={4} maxLength={2000}
                                    placeholder="Ce que vous avez apprécié, ce qui pourrait être amélioré…"
                                    style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e7e2da', borderRadius: 8, padding: '10px 12px', fontSize: 14, color: '#1c1a17', outline: 'none', resize: 'vertical', fontFamily: 'inherit', marginBottom: comment ? 6 : 18 }} />
                                {comment && (
                                    <p style={{ fontSize: 11.5, color: '#a8a29a', margin: '0 0 18px', lineHeight: 1.5 }}>
                                        Vous pouvez modifier ce texte librement : c'est lui qui sera publié.
                                    </p>
                                )}
                            </>
                        ) : null /* l'assistant est à l'écran : c'est lui qui porte le choix « j'écris moi-même » */}

                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', marginBottom: showName ? 12 : 22 }}>
                            <input type="checkbox" checked={showName} onChange={(e) => setShowName(e.target.checked)} style={{ marginTop: 3, accentColor: brandColor }} />
                            <span style={{ fontSize: 13, color: '#3d3a35', lineHeight: 1.5 }}>Afficher mon nom à côté de mon avis</span>
                        </label>
                        {showName && (
                            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={120}
                                placeholder="Nom affiché"
                                style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e7e2da', borderRadius: 8, padding: '10px 12px', fontSize: 14, color: '#1c1a17', outline: 'none', marginBottom: 22 }} />
                        )}

                        <button onClick={handleSubmit} disabled={submitting || rating < 1}
                            style={{ width: '100%', background: brandColor, color: '#fff', fontWeight: 600, fontSize: 15, padding: '13px 24px', borderRadius: 8, border: 'none', cursor: rating < 1 ? 'not-allowed' : 'pointer', opacity: (submitting || rating < 1) ? 0.55 : 1 }}>
                            {submitting ? 'Envoi…' : 'Envoyer mon avis'}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};
